import React from 'react';

const ASTRA_TERMS_URL = 'https://astrafi.com/terms/';
const ASTRA_PRIVACY_URL = 'https://astrafi.com/privacy/';

type Props = {
  visible: boolean;
  bankName?: string;
  last4?: string;
  onClose: () => void;
  onContinue: () => void;
  busy?: boolean;
};

const AstraAuthorizationSheet: React.FC<Props> = ({
  visible,
  bankName,
  last4,
  onClose,
  onContinue,
  busy = false,
}) => {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-md rounded-[1.9rem] border border-slate-200 bg-white shadow-2xl overflow-hidden">
        <div className="px-5 pt-4 pb-2 flex justify-center">
          <div className="h-1.5 w-12 rounded-full bg-slate-200" />
        </div>
        <div className="px-5 pb-6">
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full border border-slate-200 text-slate-500 text-sm"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <h3 className="mt-1 text-[28px] leading-[1.05] font-black tracking-tight text-slate-900">
            Finish bank pay setup
          </h3>
          <p className="mt-3 text-[14px] leading-6 text-slate-500">
            To pay from {bankName || 'your bank'}{last4 ? ` •••• ${last4}` : ''}, you&apos;ll complete a quick one-time identity verification. This usually takes about a minute. Future bank payments will be faster after this.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {['About 1 min', 'One-time setup', 'Secure with Astra'].map((label) => (
              <div key={label} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-bold text-slate-600">
                {label}
              </div>
            ))}
          </div>

          <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">What happens next</p>

          <div className="mt-3 space-y-3">
            {[
              ['1', 'Confirm your identity', "Confirm it's really you"],
              ['2', 'Approve bank pay', 'Authorize this bank account to be used for payments'],
              ['3', 'Return to Payment', "You can continue your payment when verified."],
            ].map(([index, title, subtitle]) => (
              <div key={index} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-3">
                <div className="w-8 h-8 rounded-full bg-slate-900 text-white text-[12px] font-bold flex items-center justify-center shrink-0">
                  {index}
                </div>
                <div>
                  <p className="text-[14px] font-bold text-slate-900">{title}</p>
                  <p className="text-[12px] text-slate-500 mt-0.5">{subtitle}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={onContinue}
            disabled={busy}
            className={`mt-6 w-full h-12 rounded-2xl font-bold text-sm ${busy ? 'bg-slate-200 text-slate-400' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
          >
            {busy ? 'Continuing...' : 'Continue'}
          </button>

          <p className="mt-4 text-center text-[12px] text-slate-500">Securely powered by Astra</p>
          <p className="mt-2 text-center text-[11px] leading-5 text-slate-400">
            By continuing, you agree to Astra&apos;s{' '}
            <a className="text-slate-600 underline" href={ASTRA_TERMS_URL} target="_blank" rel="noreferrer">
              Terms of Service
            </a>
            {' '}and{' '}
            <a className="text-slate-600 underline" href={ASTRA_PRIVACY_URL} target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default AstraAuthorizationSheet;
